import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import Grid from '@mui/material/Grid';

import {
  useGetWatchListQuery,
  useDeleteMovieMutation,
  useWatchlistTvNotificationsQuery,
} from '../actions/apiSlice';

function getBetterImageUrl(url) {
  if (!url) return '';
  return url.replace(/UX\d+/, 'UX342').replace(/QL\d+/, 'QL100');
}

function formatDate(date) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(date).toLocaleDateString(undefined, options);
}

function AirDate({ date, ...sx }) {
  const aired = new Date(date) < new Date();
  return (
    <Box
      component="span"
      sx={{ color: aired ? 'success.main' : 'error.main', fontWeight: 600, ...sx }}
    >
      {formatDate(date)}
    </Box>
  );
}

function WatchlistScreen() {
  const [deletingMovieId, setDeletingMovieId] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [activeTab, setActiveTab] = useState('movies');
  const navigate = useNavigate();

  const { data, error, isLoading } = useGetWatchListQuery();
  const { data: tvEpisodesThisWeek = [] } = useWatchlistTvNotificationsQuery();
  const [deleteMovie] = useDeleteMovieMutation();

  const navigateShow = (show) => {
    const q = `${show.name} ${show.year}`;
    // Resolve by id so titles like "Next" aren't re-guessed from text. Watchlist
    // TV rows store tmdb_id; movies store the IMDb id (title_id) instead.
    let idParam = '';
    if (show.tmdb_id) {
      idParam = `&${show.is_movie ? 'mid' : 'tid'}=${show.tmdb_id}`;
    } else if (show.title_id) {
      idParam = `&imdb=${show.title_id}`;
    }
    navigate(`/details/?q=${q}${idParam}`);
  };

  const handleDelete = (movieId) => {
    setDeletingMovieId(movieId);
    deleteMovie(movieId).finally(() => setDeletingMovieId(null));
  };

  const movies = data ? data.filter((i) => i.is_movie) : [];
  const tvShows = data ? data.filter((i) => !i.is_movie) : [];
  const movieStreamingNow = data ? data.filter((i) => i.is_movie && i.is_streaming) : [];

  const isMoviesTab = activeTab === 'movies';
  const notificationsCount = isMoviesTab ? movieStreamingNow.length : tvEpisodesThisWeek.length;
  const items = isMoviesTab ? movies : tvShows;

  return (
    <>
      <Helmet>
        <title>Watchlist | AirCheck</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              Your watchlist
            </Typography>
            <Typography color="text.secondary">
              Movies and shows you've saved for later.
            </Typography>
          </Box>
          <IconButton onClick={(e) => setNotifAnchor(e.currentTarget)}>
            <Badge badgeContent={notificationsCount} color="primary">
              <NotificationsActiveIcon />
            </Badge>
          </IconButton>
        </Stack>

        <Popover
          open={Boolean(notifAnchor)}
          anchorEl={notifAnchor}
          onClose={() => setNotifAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 340, p: 2, borderRadius: 3 } }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {isMoviesTab ? 'Now streaming' : "This week's episodes"}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          {isMoviesTab ? (
            movieStreamingNow.length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No streaming updates yet.
              </Typography>
            ) : (
              movieStreamingNow.map((movie) => (
                <Box
                  key={movie.id}
                  onClick={() => navigateShow(movie)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                  }}
                >
                  <Avatar
                    variant="rounded"
                    src={movie.small_poster_url}
                    alt={movie.name}
                    sx={{ width: 44, height: 64 }}
                  />
                  <Typography variant="body2">
                    {movie.name} is now streaming
                  </Typography>
                </Box>
              ))
            )
          ) : tvEpisodesThisWeek.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No new episodes this week.
            </Typography>
          ) : (
            tvEpisodesThisWeek.map((ep) => (
              <Box
                key={ep.id}
                onClick={() => navigateShow(ep)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
                }}
              >
                <Avatar
                  variant="rounded"
                  src={ep.small_poster_url}
                  alt={ep.name}
                  sx={{ width: 44, height: 64 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {ep.name} — S{ep.season}E{ep.episode}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(ep.air_date) < new Date() ? 'Aired' : 'Airs'} <AirDate date={ep.air_date} />
                  </Typography>
                </Box>
              </Box>
            ))
          )}
          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={() => setNotifAnchor(null)}
            sx={{ mt: 1.5 }}
          >
            Close
          </Button>
        </Popover>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 3, borderBottom: (t) => `1px solid ${t.palette.divider}` }}
        >
          <Tab value="movies" label={`Movies (${movies.length})`} />
          <Tab value="tvshows" label={`TV shows (${tvShows.length})`} />
        </Tabs>

        {isLoading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={i}>
                <Skeleton variant="rounded" sx={{ width: '100%', aspectRatio: '2 / 3', borderRadius: 2 }} />
                <Skeleton sx={{ mt: 1, width: '80%' }} />
              </Grid>
            ))}
          </Grid>
        ) : error ? (
          <Typography color="error">Error: {error.message}</Typography>
        ) : items.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              border: (t) => `1px dashed ${t.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Nothing here yet
            </Typography>
            <Typography color="text.secondary">
              Save {isMoviesTab ? 'movies' : 'shows'} from any detail page to see them here.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    aspectRatio: '2 / 3',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    transition: 'transform .25s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                    '&:hover img': { transform: 'scale(1.05)' },
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                  onClick={() => navigateShow(item)}
                >
                  <Box
                    component="img"
                    src={getBetterImageUrl(item.small_poster_url)}
                    alt={item.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform .35s ease',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      backdropFilter: 'blur(6px)',
                      '&:hover': { bgcolor: 'rgba(229,9,20,0.9)' },
                    }}
                  >
                    {deletingMovieId === item.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RemoveCircleOutlineOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.9) 100%)',
                      display: 'flex',
                      alignItems: 'flex-end',
                      p: 1.25,
                    }}
                  >
                    <Box sx={{ color: '#fff', width: '100%' }}>
                      <Typography variant="body2" noWrap fontWeight={700}>
                        {item.name}
                      </Typography>
                      {item.is_movie && item.air_date && (
                        <Typography variant="caption" sx={{ opacity: 0.95 }}>
                          <AirDate date={item.air_date} />
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </>
  );
}

export default WatchlistScreen;
